import React, {Fragment} from 'react'
import {StyleSheet, Text, View} from '@react-pdf/renderer'
import {IIncomeDocument} from '../IncomeDocumentDetailPage'
import {indexOf} from 'lodash'
import {numberFormat} from '../../../utils/Functions'

interface IPurchaseOrderTable {
  document: IIncomeDocument
}

const PurchaseOrderTable: React.FC<IPurchaseOrderTable> = (props) => {
  const { document } = props

  const style = StyleSheet.create({
    productColumn: {
      display: 'flex',
      flexDirection: 'column',
    },
    productColumnHeader: {
      flex: 1,
      paddingVertical: '4px',
      paddingHorizontal: '8px',
      color: '#2D3889',
      textAlign: 'center',
      backgroundColor: 'rgba(45,56,137,0.2)',
      borderRight: '1px solid #2D3889',
      borderBottom: '1px solid #2D3889',
    },
    productRowContent: {
      flex: 1,
      paddingVertical: '2px',
      paddingHorizontal: '8px',
      borderRight: '1px solid #2D3889',
      textAlign: 'right',
    }
  })

  const _renderProductHeader = () => {
    const _productHeaderColumn = (label: string, compStyle?: {[key: string]: any}) => {
      return (
        <View style={[style.productColumnHeader, compStyle || {}]}>
          <Text>{label}</Text>
        </View>
      )
    }

    // รายการเยอะจนขึ้นหน้าใหม่: หัวตารางซ้ำทุกหน้า และแถวห้ามแยกครึ่งข้ามหน้า
    // (เดิมรหัสค้างท้ายหน้าแรก ชื่อสินค้าไปอยู่หน้าถัดไป) (29 ก.ย. 2569)
    return (
      <View fixed style={{display: 'flex', flexDirection: 'row', width: '100%'}}>
        {_productHeaderColumn('รหัสสินค้า', {flex: 1.2})}
        {_productHeaderColumn('รายการ', {flex: 3})}
        {_productHeaderColumn('คลัง', {flex: 1.2})}
        {_productHeaderColumn('หน่วย')}
        {_productHeaderColumn('จำนวน')}
        {_productHeaderColumn('ราคา/หน่วย')}
        {_productHeaderColumn('รวม VAT', {borderRight: 'none'})}
      </View>
    )
  }

  const _renderProductRow = (product: any, isLast: boolean) => {
    const productCode = product.inventoryProduct?.code || product.treatmentItem?.barcode || product.treatmentItem?.code
    const productName = product.inventoryProduct?.nameTh || product.inventoryProduct?.nameEn || product.treatmentItem?.nameTh || product.treatmentItem?.nameEn
    const warehouseName = product.inventoryProduct?.warehouse?.name || '-'
    const unitName = product.inventoryProduct?.unit || product.treatmentItem?.unit || '-'

    const _renderProductRowContent = (label: string, compStyle?: {[key: string]: any}) => {
      return (
        <View style={[
          style.productRowContent,
          isLast ? {borderBottom: '1px solid #2D3889'} : {},
          compStyle || {}
        ]}>
          <Text>{label}</Text>
        </View>
      )
    }

    return (
      <View wrap={false} style={{display: 'flex', flexDirection: 'row', width: '100%'}}>
        {_renderProductRowContent(productCode, {flex: 1.2, textAlign: 'left'})}
        {_renderProductRowContent(productName, {flex: 3, textAlign: 'left'})}
        {_renderProductRowContent(warehouseName, {flex: 1.2, textAlign: 'center'})}
        {/* เดิมช่อง "หน่วย" แสดงราคาต่อหน่วย และ "ราคารวม VAT" กับ "รวม" เป็นค่าเดียวกัน (5 ต.ค. 2569) */}
        {_renderProductRowContent(unitName, {textAlign: 'center'})}
        {_renderProductRowContent(numberFormat(product.quantity))}
        {_renderProductRowContent(numberFormat(product.pricePerUnit))}
        {_renderProductRowContent(numberFormat(product.beforeVatPrice + product.vatPrice), {borderRight: 'none'})}
        {/* {_renderProductRowContent(numberFormat((product.beforeVatPrice + product.vatPrice) * product.quantity), {borderRight: 'none'})} */}
      </View>
    )
  }

  const _renderProductSum = () => {
    const branchPart = document.branch?.name ? `สาขา${document.branch.name}` : ''
    const deptPart = document.department?.name ? `แผนก${document.department.name}` : ''
    const requesterLabel = [branchPart, deptPart].filter(Boolean).join(' ') || '-'
    return (
      <View wrap={false} style={{display: 'flex', flexDirection: 'row', width: '100%', borderBottom: '1px solid #2D3889'}}>
        <View style={{width: 267.5, paddingVertical: '4px', paddingHorizontal: '8px', borderRight: '1px solid #2D3889'}}>
          <Text>{`ผู้ขอซื้อ : ${requesterLabel}`}</Text>
        </View>
        <View style={{width: 50, paddingVertical: '4px', paddingHorizontal: '8px', borderRight: '1px solid #2D3889', color: '#2D3889', textAlign: 'center', backgroundColor: 'rgba(45,56,137,0.2)'}}>
          <Text>{'ส่วนลด'}</Text>
        </View>
        <View style={{width: 72.5, paddingVertical: '4px', paddingHorizontal: '8px', borderRight: '1px solid #2D3889', textAlign: 'right'}}>
          <Text>{numberFormat(document.discountPrice)}</Text>
        </View>
        <View style={{width: 145.25, paddingVertical: '4px', paddingHorizontal: '8px', borderRight: '1px solid #2D3889', color: '#2D3889', textAlign: 'center', backgroundColor: 'rgba(45,56,137,0.2)'}}>
          <Text>{'ยอดรวม'}</Text>
        </View>
        <View style={{width: 71.5, paddingVertical: '4px', paddingHorizontal: '8px', textAlign: 'right'}}>
          <Text>{numberFormat(document.totalAmount)}</Text>
        </View>
      </View>
    )
  }

  const _renderPersonal = () => {
    return (
      <View wrap={false} style={{display: 'flex', flexDirection: 'row', borderBottom: '1px solid #2D3889', minHeight: 40}}>
        <View style={{flex: 1, paddingVertical: '4px', paddingHorizontal: '8px', borderRight: '1px solid #2D3889'}}>
          <Text>{`ผู้ขอซื้อ : ${document?.actionBy?.fullName || ''}`}</Text>
        </View>
        <View style={{flex: 1, paddingVertical: '4px', paddingHorizontal: '8px', borderRight: '1px solid #2D3889'}}>
          <Text>{`ผู้้ตรวจสอบ :`}</Text>
        </View>
        <View style={{flex: 1, paddingVertical: '4px', paddingHorizontal: '8px'}}>
          <Text>{`ผู้อนุมัติ :`}</Text>
        </View>
      </View>
    )
  }

  return (
    <View style={{flex: 1, width: '100%', border: '1px solid #2D3889', marginTop: 12}}>
      {_renderProductHeader()}
      {document.incomeDocumentItems.map((product, index: number) => {
        const isLast = document.incomeDocumentItems.length === index + 1

        return (
          <Fragment key={`item-${index}`}>
            {_renderProductRow(product, isLast)}
          </Fragment>
        )
      })}
      {_renderProductSum()}
      {_renderPersonal()}
      <View style={{display: 'flex', flexDirection: 'row'}}>
        <View style={{flex: 1, paddingVertical: '4px', paddingHorizontal: '8px'}}>
          <Text>{`หมายเหตุ : ${document.linkingDocument?.code || '-'}`}</Text>
        </View>
      </View>
    </View>
  )
}

export default PurchaseOrderTable
